import { Fragment, useEffect, useState } from "react";
import ReactAudioPlayer from "react-audio-player";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import swal from "@sweetalert/with-react";
import BigLoader from "../../../components/BigLoader";
import Button from "../../../components/Button";
import Card from "../../../components/Card";
import Dropzone from "../../../components/Dropzone";
import Editor from "../../../components/Editor";
import Loader from "../../../components/Loader";
import TextInput from "../../../components/TextInput";
import { setInterface } from "../../../redux/slices/interfaceSlice";
import service from "../../../service";

export default function ComposeDeepReadBab({ editMode = false }) {
  const dispatch = useDispatch();
  const { id, bookId } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [loadingContent, setLoadingContent] = useState(editMode);
  const [bookInfo, setBookInfo] = useState(null);
  const [meta, setMeta] = useState({
    deepReadId: id,
    bookId: bookId,
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      title: "",
      body: "",
      audio: null,
    },
  });

  const _proceed = ({ title, body, audio }) => {
    setLoading(true);

    const formData = new FormData();

    formData.append("title", title);
    formData.append("body", body);
    if (audio instanceof File) {
      formData.append("audio", audio);
    }

    const url = editMode
      ? `/api/deep-read/rings/${id}`
      : `/api/deep-read/books/${bookId}/rings`;

    service({
      url,
      data: formData,
      method: editMode ? "PUT" : "POST",
    })
      .then((response) => {
        const { id: ringId, audio: afterAudio } = response.data;
        setLoading(false);
        swal(
          "Berhasil",
          editMode ? "Ring berhasil disunting" : "Ring telah ditambahkan",
          "success"
        ).then((value) => {
          if (value && !editMode) {
            const deepReadTarget =
              meta.deepReadId || response.data.deep_read_id || id;
            const bookTarget =
              meta.bookId || response.data.deep_read_book_id || bookId;
            history.replace(`/deep-read/${deepReadTarget}/book/${bookTarget}/ring`);
          } else if (audio && afterAudio) {
            setValue("audio", afterAudio);
          }
        });
      })
      .catch(() => {
        setLoading(false);
      });
  };

  // Set base page title
  useEffect(() => {
    dispatch(
      setInterface({
        active: "Deep Read",
        pageTitle: editMode ? "Edit Ring" : "Tambah Ring",
      })
    );
  }, [dispatch, editMode]);

  useEffect(() => {
    if (editMode) {
      service
        .get("/api/deep-read/rings/" + id)
        .then((response) => {
          const { title, body, audio, deep_read_id, deep_read_book_id } =
            response.data;
          setLoadingContent(false);
          setMeta({
            deepReadId: deep_read_id || id,
            bookId: deep_read_book_id || bookId,
          });
          reset({
            title,
            body,
            audio,
          });
        })
        .catch((e) => {
          setLoadingContent(false);
          if (e.response?.status === 404) {
            history.replace("/deep-read");
          }
        });
    }
  }, [editMode, id, history, reset]);

  useEffect(() => {
    if (bookId) {
      service
        .get(`/api/deep-read/${id}/books/${bookId}`)
        .then((res) => {
          setBookInfo(res.data);
          dispatch(
            setInterface({
              active: "Deep Read",
              pageTitle: `${editMode ? "Edit Ring" : "Tambah Ring"} · ${
                res.data?.title || ""
              }`,
            })
          );
        })
        .catch(() => {});
    }
  }, [id, bookId, dispatch, editMode]);

  if (loadingContent) {
    return (
      <div className="h-64 flex justify-center items-center flex-col">
        <Loader className="bg-black-100" />
        <span className="mt-3 text-black-300 text-sm">Memuat</span>
      </div>
    );
  }

  return (
    <Fragment>
      <Helmet>
        <title>{(editMode ? "Edit" : "Tambah") + " Ring"}</title>
      </Helmet>
      <form
        className="flex flex-col lg:flex-row"
        onSubmit={handleSubmit(_proceed)}
      >
        <Card className="w-full lg:w-7/12">
          <TextInput
            label="Judul Ring"
            containerClassName="mb-4"
            message={errors.title?.message}
            {...register("title", { required: "Tidak boleh kosong" })}
          />
          <Controller
            control={control}
            name="body"
            rules={{ required: "Tidak boleh kosong" }}
            render={({ field: { value, onChange } }) => (
              <Editor
                label="Isi Ring"
                containerClassName="mb-4"
                value={value || ""}
                message={errors.body?.message}
                onChange={(_, __, ___, editor) => {
                  onChange(editor.getHTML());
                }}
              />
            )}
          />
          <Button type="submit" className="w-24 hidden lg:block">
            {editMode ? "Edit" : "Tambah"}
          </Button>
        </Card>
        <div className="flex-1 ml-0 mt-5 lg:ml-5 lg:mt-0">
          <Card>
            <Controller
              control={control}
              name="audio"
              rules={{ required: !editMode }}
              render={({ field: { onChange } }) => (
                <Dropzone
                  label="File Audio"
                  dropzone={{
                    accept: "audio/mpeg, audio/ogg, audio/wav, audio/aac",
                  }}
                  message={errors.audio?.message}
                  containerClassName="mb-4"
                  onChange={(accepted) => {
                    onChange(accepted[0]);
                  }}
                />
              )}
            />

            {watch("audio") && (
              <Fragment>
                <span className="text-sm font-semibold text-black-300 mb-2 block">Preview</span>
                <div className="flex justify-center">
                  <ReactAudioPlayer
                    className="w-full mt-2"
                    src={
                      watch("audio") instanceof File
                        ? URL.createObjectURL(watch("audio"))
                        : process.env.REACT_APP_BASE_URL + watch("audio")
                    }
                    controls
                  />
                </div>
              </Fragment>
            )}
          </Card>
        </div>
        <Card className="block lg:hidden mt-5">
          <Button type="submit" className="w-24">
            {editMode ? "Edit" : "Tambah"}
          </Button>
        </Card>
        {loading && <BigLoader />}
      </form>
    </Fragment>
  );
}
